// StepDetailModal — T-016 Part B / T-035 B9. Shows the step's name,
// description, cues and the three standards in the active locale, plus a
// "start this" action and a close button.
//
// B9: native <dialog> with showModal() gives us focus trap, Escape, and
// inert backdrop for free. The parent still owns open/close state.

import { useEffect, useRef } from "react";
import type { JSX } from "react";
import { useI18n } from "../../i18n";
import type { Exercise, ExerciseProgress } from "../../types/calisthenics";
import type { NodeState } from "./SkillTreeViewer";
import { ExerciseMedia } from "../common/ExerciseMedia";

export interface StepDetailModalProps {
  exercise: Exercise | null;
  state: NodeState;
  progress?: ExerciseProgress;
  onClose: () => void;
  onStart?: (exercise: Exercise) => void;
}

export function StepDetailModal({
  exercise,
  state,
  progress,
  onClose,
  onStart,
}: StepDetailModalProps): JSX.Element | null {
  const { t, locale } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    closeRef.current?.focus();
    return () => {
      if (dialog.open) dialog.close();
    };
  }, []);

  const onBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) onClose();
  };

  if (!exercise) return null;

  return (
    <dialog
      ref={dialogRef}
      aria-label={`${t("common.step")} ${exercise.stepNumber}`}
      data-testid="step-detail-modal"
      className="fixed inset-0 z-50 m-auto max-h-[90vh] w-full max-w-[640px] overflow-y-auto border border-[var(--color-line)] bg-[var(--color-slab)] p-6 backdrop:bg-[var(--color-void)]/85"
      style={{ borderRadius: "var(--radius-card)" }}
      onClick={onBackdropClick}
      onClose={onClose}
    >
      <div
        className="m-4 max-h-[90vh] w-full max-w-[640px] overflow-y-auto border border-[var(--color-line)] bg-[var(--color-slab)] p-6"
        style={{ borderRadius: "var(--radius-card)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4">
          <div>
            <p className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-mute)]">
              {t("common.step")} {exercise.stepNumber}
            </p>
            <h2 className="font-display mt-1 text-3xl uppercase tracking-[0.02em] text-[var(--color-ink-hi)]">
              {exercise.name[locale]}
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t("treeModal.close")}
            data-testid="step-detail-close"
            className="border border-[var(--color-line-strong)] px-3 text-[var(--color-ink)] hover:bg-[var(--color-slab-2)]"
            style={{ minHeight: "44px", borderRadius: "var(--radius-sharp)" }}
          >
            ✕
          </button>
        </header>
        {/* ExerciseMedia owns its own mediaBroken state and is keyed by
            exercise.id, so the flag really does reset on every exercise
            change (see ExerciseMedia.tsx for the long version). */}
        <ExerciseMedia
          key={exercise.id}
          exercise={exercise}
          locale={locale}
          imgClassName="max-h-56 w-auto rounded-[var(--radius-sharp)] object-contain"
        />
        <p
          className="mt-4 font-sans text-base text-[var(--color-ink)]"
          style={{ lineHeight: 1.5 }}
        >
          {exercise.description[locale]}
        </p>
        <ul className="mt-4 list-disc pl-5 font-sans text-sm text-[var(--color-ink)]">
          {exercise.cues[locale].map((cue, i) => (
            <li key={i}>{cue}</li>
          ))}
        </ul>
        <section
          aria-label={t("treeModal.standards")}
          className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3"
        >
          <Standard
            label={t("treeModal.beginner")}
            value={
              (exercise.standard.holdSeconds ?? 0) > 0
                ? `${exercise.standard.beginnerHoldSeconds ?? 0}s`
                : `${exercise.standard.beginnerReps}`
            }
            active={progress && progress.bestReps >= exercise.standard.beginnerReps}
          />
          <Standard
            label={t("treeModal.intermediate")}
            value={
              (exercise.standard.holdSeconds ?? 0) > 0
                ? `${exercise.standard.intermediateHoldSeconds ?? 0}s`
                : `${exercise.standard.intermediateReps}`
            }
            active={progress && progress.bestReps >= exercise.standard.intermediateReps}
          />
          <Standard
            label={t("treeModal.mastery")}
            value={
              (exercise.standard.holdSeconds ?? 0) > 0
                ? `${exercise.standard.holdSeconds}s`
                : `${exercise.standard.masteryReps}`
            }
            active={
              (exercise.standard.holdSeconds ?? 0) > 0
                ? (progress?.bestHoldSeconds ?? 0) >= (exercise.standard.holdSeconds ?? Infinity)
                : (progress?.bestReps ?? 0) >= exercise.standard.masteryReps
            }
            mastery
          />
        </section>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <span
            className="font-subhead text-xs uppercase tracking-[0.06em]"
            style={{
              color:
                state === "mastery"
                  ? "var(--color-gold)"
                  : state === "locked"
                    ? "var(--color-ink-dim)"
                    : "var(--color-lime)",
            }}
          >
            {t(
              state === "mastery"
                ? "treeState.mastery"
                : state === "intermediate"
                  ? "treeState.intermediate"
                  : state === "beginner"
                    ? "treeState.beginner"
                    : state === "inProgress"
                      ? "treeState.inProgress"
                      : state === "available"
                        ? "treeState.available"
                        : "treeState.locked",
            )}
          </span>
          {onStart && state !== "locked" && (
            <button
              type="button"
              onClick={() => onStart(exercise)}
              data-testid="step-detail-start"
              className="font-display uppercase tracking-[0.02em] text-[var(--color-void)]"
              style={{
                minHeight: "64px",
                padding: "0 24px",
                backgroundColor: "var(--color-lime)",
                borderRadius: "var(--radius-sharp)",
                fontSize: 18,
              }}
            >
              {t("treeModal.startThis")}
            </button>
          )}
        </div>
      </div>
    </dialog>
  );
}

function Standard({
  label,
  value,
  active,
  mastery,
}: {
  label: string;
  value: string;
  active?: boolean;
  mastery?: boolean;
}): JSX.Element {
  return (
    <div
      className="flex flex-col gap-1 border p-3"
      style={{
        borderColor: active ? (mastery ? "var(--color-gold)" : "var(--color-lime)") : "var(--color-line)",
        backgroundColor: active
          ? mastery
            ? "var(--color-lime)"
            : "rgba(198,255,61,0.10)"
          : "transparent",
        borderRadius: "var(--radius-card)",
        color: active && mastery ? "var(--color-void)" : "var(--color-ink-hi)",
      }}
    >
      <span className="font-subhead text-[10px] uppercase tracking-[0.06em] opacity-80">
        {label}
      </span>
      <span
        className="font-numeric tabular"
        style={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}
      >
        {value}
      </span>
    </div>
  );
}
